import i18n from "../../../i18n";
import React, { useState, useEffect } from "react";
import { useFormatter } from "../../../hooks/useFomatter";
import { useServerApi } from "../../../hooks/userServerApi";
import { IconDownload, IconCheck, IconChevronDown, IconCircleCheck, IconExclamationMark } from "@tabler/icons";
import {
  Button,
  Text,
  Title,
  Badge,
  Stack,
  Group,
  Grid,
  Box,
  Paper,
  Modal,
  List,
  Checkbox,
  Menu,
  Textarea,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import moment from "moment";
import DuplicateBidConflictsAlert from "./DuplicateBidConflictsAlert";
import { openFileUrl } from "../../../utils/openReport";

const ConfirmationTabContent = ({
  form,
  bids,
  summary,
  loading,
  negativeBalancePreview = [],
  onConfirm,
}) => {
  const [api] = useServerApi();
  const [formatter] = useFormatter();
  const { t } = useTranslation();
  const [reportLoading, setReportLoading] = useState(false);
  const [walletReportLoading, setWalletReportLoading] = useState(false);
  const [mriLoading, setMriLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);
  const [
    mriConfigWarningOpened,
    { open: openMriConfigWarning, close: closeMriConfigWarning },
  ] = useDisclosure(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [settleLoading, setSettleLoading] = useState(false);
  const [settlePayDate, setSettlePayDate] = useState(null);
  const [settleRemark, setSettleRemark] = useState("");
  const [
    settleModalOpened,
    { open: openSettleModal, close: closeSettleModal },
  ] = useDisclosure(false);
  const [duplicateConflicts, setDuplicateConflicts] = useState([]);

  useEffect(() => {
    const loadDuplicateConflicts = async () => {
      const payrollId = form?.values?._id;
      if (!payrollId) {
        setDuplicateConflicts([]);
        return;
      }
      try {
        const result = await api.Payroll2.getDuplicateBidConflicts(payrollId);
        if (result?.success) {
          setDuplicateConflicts(
            result.data?.excludedConflicts || result.data?.conflicts || [],
          );
        }
      } catch (error) {
        console.log("loadDuplicateConflicts error:", error);
      }
    };
    loadDuplicateConflicts();
  }, [form?.values?._id]);

  const totalBids = form?.values?.payrollBidsCount ?? bids?.length ?? 0;
  const pendingBids =
    form?.values?.pendingCount ??
    bids?.filter((b) => b.status === "PENDING")?.length ??
    0;
  const holdBids =
    form?.values?.holdCount ??
    bids?.filter((b) => b.status === "HOLD")?.length ??
    0;
  const settledBids =
    form?.values?.settledCount ??
    bids?.filter((b) => b.status === "SETTLED")?.length ??
    0;
  const totalAmount =
    (form?.values?.totalPendingAmount ?? 0) +
      (form?.values?.totalHoldAmount ?? 0) +
      (form?.values?.totalSettledAmount ?? 0) ||
    bids?.reduce((sum, b) => sum + (b.commissionAmount || 0), 0) ||
    0;
  const pendingAmount =
    form?.values?.totalPendingAmount ??
    bids
      ?.filter((b) => b.status === "PENDING")
      ?.reduce((sum, b) => sum + (b.commissionAmount || 0), 0) ??
    0;
  const holdingAmount =
    form?.values?.totalHoldAmount ??
    bids
      ?.filter((b) => b.status === "HOLD")
      ?.reduce((sum, b) => sum + (b.commissionAmount || 0), 0) ??
    0;
  const settledAmount =
    form?.values?.totalSettledAmount ??
    bids
      ?.filter((b) => b.status === "SETTLED")
      ?.reduce((sum, b) => sum + (b.commissionAmount || 0), 0) ??
    0;

  const clientPaymentBids =
    bids?.filter((b) => b.sourceType === "CLIENTPAYMENT") || [];
  const commStatementBids =
    bids?.filter((b) => b.sourceType === "COMMISSIONSTATEMENT") || [];
  const adjustmentBids =
    bids?.filter((b) => b.sourceType === "ADJUSTMENT") || [];

  const clientPaymentTotal = clientPaymentBids.reduce(
    (sum, b) => sum + (b.commissionAmount || 0),
    0,
  );
  const commStatementTotal = commStatementBids.reduce(
    (sum, b) => sum + (b.commissionAmount || 0),
    0,
  );
  const adjustmentTotal = adjustmentBids.reduce(
    (sum, b) => sum + (b.commissionAmount || 0),
    0,
  );

  const walletsToAutoHold = negativeBalancePreview || [];
  const pendingSettleBidCount = Math.max(
    0,
    pendingBids -
      walletsToAutoHold.reduce((sum, w) => sum + (w.bidCount || 0), 0),
  );
  const pendingSettleAmount = Math.round(
    (pendingAmount -
      walletsToAutoHold.reduce((sum, w) => sum + (w.netAmount || 0), 0)) *
      100,
  ) / 100;

  const handleGenerateReport = async () => {
    try {
      setReportLoading(true);
      const payrollId = form?.values?._id;
      if (!payrollId) {
        console.log("No payroll ID");
        return;
      }

      const result = await api.Payroll2.getPayrollReport(payrollId);
      if (result?.success && result?.data?.url) {
        await openFileUrl(result.data.url, { mode: "download" });
      } else {
        showNotification({
          title: "Download failed",
          message: result?.message || "Unable to generate payroll report.",
          color: "red",
        });
      }
    } catch (error) {
      console.log("Error generating report:", error);
      showNotification({
        title: "Download failed",
        message: error?.message || "Unable to download payroll report.",
        color: "red",
      });
    } finally {
      setReportLoading(false);
    }
  };

  const handleGenerateWalletReport = async (settledOnly = false) => {
    try {
      setWalletReportLoading(true);
      const payrollId = form?.values?._id;
      if (!payrollId) {
        console.log("No payroll ID");
        return;
      }

      const result = await api.Payroll2.getWalletReport(payrollId, {
        settledOnly,
      });
      if (result?.success && result?.data?.url) {
        await openFileUrl(result.data.url, { mode: "download" });
      } else {
        showNotification({
          title: "Download failed",
          message: result?.message || "Unable to generate wallet reports.",
          color: "red",
        });
      }
    } catch (error) {
      console.log("Error generating wallet report:", error);
      showNotification({
        title: "Download failed",
        message: error?.message || "Unable to download wallet reports.",
        color: "red",
      });
    } finally {
      setWalletReportLoading(false);
    }
  };

  const handleGenerateHsbcMri = async () => {
    try {
      setMriLoading(true);
      const payrollId = form?.values?._id;
      if (!payrollId) {
        console.log("No payroll ID");
        return;
      }

      const result = await api.Payroll2.getHsbcMriFile(payrollId);
      if (result?.success) {
        const skipped = result.data.skipped || [];
        const holdExcludedCount = result.data.holdExcludedCount || 0;
        let message = t("payroll2.confirmation.hsbcMriSuccess");
        if (holdExcludedCount > 0) {
          message += ` ${t("payroll2.confirmation.hsbcMriHoldingExcluded", {
            count: holdExcludedCount,
          })}`;
        }
        if (skipped.length > 0) {
          const skippedDetails = skipped
            .map((item) => `${item.walletName}: ${item.reason}`)
            .join("\n");
          message += ` ${t("payroll2.confirmation.hsbcMriSkipped", {
            count: skipped.length,
          })}\n${skippedDetails}`;
        }

        showNotification({
          title: t("payroll2.confirmation.hsbcMri"),
          color: skipped.length > 0 ? "yellow" : "green",
          icon: <IconCircleCheck size={18} />,
          message,
          autoClose: skipped.length > 0 ? 10000 : 5000,
        });
      } else {
        const isConfigMissing =
          result?.error?.code === "HSBC_MRI_NOT_CONFIGURED" ||
          result?.message === "ERR_HSBC_MRI_NOT_CONFIGURED";

        if (isConfigMissing) {
          openMriConfigWarning();
          return;
        }

        const errorMessage =
          result?.error?.message ||
          result?.message ||
          result?.data?.message ||
          t("payroll2.confirmation.hsbcMriError");
        showNotification({
          title: t("payroll2.confirmation.hsbcMri"),
          color: "red",
          icon: <IconExclamationMark size={18} />,
          message: errorMessage,
        });
      }
    } catch (error) {
      console.log("Error generating HSBC MRI file:", error);
      showNotification({
        title: t("payroll2.confirmation.hsbcMri"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: t("payroll2.confirmation.hsbcMriError"),
      });
    } finally {
      setMriLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setConfirmLoading(true);
      closeConfirmModal();
      const payrollId = form?.values?._id;
      if (!payrollId) {
        console.log("No payroll ID");
        return;
      }

      const result = await api.Payroll2.confirmPayroll(payrollId);
      if (result?.success) {
        form.setFieldValue("status", "COMPLETED");
        const autoHeldWallets = result.data?.autoHeldWallets || [];
        if (autoHeldWallets.length > 0) {
          const walletDetails = autoHeldWallets
            .map(
              (w) =>
                `${w.walletName}: ${formatter.currency(w.netAmount || 0)} (${w.bidCount} bid(s))`,
            )
            .join("\n");
          showNotification({
            title: t("payroll2.confirmation.autoHeldTitle"),
            color: "orange",
            message: `${t("payroll2.confirmation.autoHeldMessage", {
              count: autoHeldWallets.length,
            })}\n${walletDetails}`,
            autoClose: 10000,
          });
        }
        if (form?.loadFromDB) {
          await form.loadFromDB();
        }
        if (onConfirm) {
          await onConfirm();
        }
      } else {
        console.log("Failed to confirm payroll:", result);
      }
    } catch (error) {
      console.log("Error confirming payroll:", error);
    } finally {
      setConfirmLoading(false);
      setConfirmChecked(false);
    }
  };

  const handleConfirmClick = () => {
    setConfirmChecked(false);
    openConfirmModal();
  };

  const handleSettleClick = () => {
    const existingPayDate = form?.values?.payDate;
    setSettlePayDate(
      existingPayDate ? moment(existingPayDate).toDate() : new Date(),
    );
    setSettleRemark(form?.values?.remark || "");
    openSettleModal();
  };

  const handleSettle = async () => {
    if (!settlePayDate) {
      showNotification({
        title: t("payroll2.confirmation.settleModalTitle"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: t("payroll2.confirmation.paidOutDateRequired"),
      });
      return;
    }

    try {
      setSettleLoading(true);
      const payrollId = form?.values?._id;
      if (!payrollId) {
        console.log("No payroll ID");
        return;
      }

      const result = await api.Payroll2.settlePayroll(payrollId, {
        payDate: moment(settlePayDate).toISOString(),
        remark: settleRemark,
      });

      if (result?.success) {
        const payroll = result.data?.payroll;
        if (payroll) {
          form.setFieldValue("payDate", payroll.payDate);
          form.setFieldValue("remark", payroll.remark);
          form.setFieldValue("paid", payroll.paid);
        }
        if (form?.loadFromDB) {
          await form.loadFromDB();
        }
        closeSettleModal();
        showNotification({
          title: t("payroll2.confirmation.settleModalTitle"),
          color: "green",
          icon: <IconCircleCheck size={18} />,
          message: t("payroll2.confirmation.settleSuccess"),
        });
      } else {
        const errorMessage =
          result?.error?.message ||
          result?.message ||
          t("payroll2.confirmation.settleError");
        showNotification({
          title: t("payroll2.confirmation.settleModalTitle"),
          color: "red",
          icon: <IconExclamationMark size={18} />,
          message: errorMessage,
        });
      }
    } catch (error) {
      console.log("Error settling payroll:", error);
      showNotification({
        title: t("payroll2.confirmation.settleModalTitle"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: t("payroll2.confirmation.settleError"),
      });
    } finally {
      setSettleLoading(false);
    }
  };

  const payrollStatus = form?.values?.status;
  const isConfirmed =
    payrollStatus === "CONFIRMED" || payrollStatus === "COMPLETED";
  const hasPaidOut = !!form?.values?.payDate;

  return (
    <Box>
      {duplicateConflicts.length > 0 && (
        <Box mb="lg">
          <DuplicateBidConflictsAlert conflicts={duplicateConflicts} />
        </Box>
      )}

      <Paper p="md" withBorder mb="lg">
        <Title order={5} mb="md">
          Payroll Summary
        </Title>

        <Grid>
          <Grid.Col span={6}>
            <Stack spacing="xs">
              <Group position="apart">
                <Text size="sm" color="dimmed">
                  Payroll Code:
                </Text>
                <Text size="sm" weight={600}>
                  {form?.values?.code || "-"}
                </Text>
              </Group>
              <Group position="apart">
                <Text size="sm" color="dimmed">
                  Status:
                </Text>
                <Badge
                  color={
                    isConfirmed
                      ? "green"
                      : payrollStatus === "PENDING"
                        ? "yellow"
                        : "gray"
                  }
                >
                  {payrollStatus || "PENDING"}
                </Badge>
              </Group>
              <Group position="apart">
                <Text size="sm" color="dimmed">
                  Total Wallets:
                </Text>
                <Text size="sm" weight={600}>
                  {summary?.length || 0}
                </Text>
              </Group>
            </Stack>
          </Grid.Col>

          <Grid.Col span={6}>
            <Stack spacing="xs">
              <Group position="apart">
                <Text size="sm" color="dimmed">
                  Total Bids:
                </Text>
                <Text size="sm" weight={600}>
                  {totalBids}
                </Text>
              </Group>
              <Group position="apart">
                <Text size="sm" color="dimmed">
                  Pending Bids:
                </Text>
                <Badge color="yellow" variant="light">
                  {pendingBids}
                </Badge>
              </Group>
              <Group position="apart">
                <Text size="sm" color="dimmed">
                  Hold Bids:
                </Text>
                <Badge color="orange" variant="light">
                  {holdBids}
                </Badge>
              </Group>
            </Stack>
          </Grid.Col>
        </Grid>
      </Paper>

      <Paper p="md" withBorder mb="lg">
        <Title order={5} mb="md">
          Amount Summary
        </Title>

        <Grid>
          <Grid.Col span={6}>
            <Stack spacing="xs">
              <Group position="apart">
                <Text size="sm" color="dimmed">
                  Total Commission Amount:
                </Text>
                <Text size="sm" weight={600} color="teal">
                  {formatter.currency(totalAmount)}
                </Text>
              </Group>
              <Group position="apart">
                <Text size="sm" color="dimmed">
                  Pending Amount:
                </Text>
                <Text size="sm" weight={500}>
                  {formatter.currency(pendingAmount)}
                </Text>
              </Group>
              <Group position="apart">
                <Text size="sm" color="dimmed">
                  Holding Amount:
                </Text>
                <Text size="sm" weight={500} color="orange">
                  {formatter.currency(holdingAmount)}
                </Text>
              </Group>
              <Group position="apart">
                <Text size="sm" color="dimmed">
                  Settled Amount:
                </Text>
                <Text size="sm" weight={500} color="green">
                  {formatter.currency(settledAmount)}
                </Text>
              </Group>
            </Stack>
          </Grid.Col>

          <Grid.Col span={6}>
            <Stack spacing="xs">
              <Group position="apart">
                <Text size="sm" color="dimmed">
                  From Client Payment:
                </Text>
                <Group spacing={4}>
                  <Badge size="sm" color="blue" variant="light">
                    {clientPaymentBids.length} bids
                  </Badge>
                  <Text size="sm">
                    {formatter.currency(clientPaymentTotal)}
                  </Text>
                </Group>
              </Group>
              <Group position="apart">
                <Text size="sm" color="dimmed">
                  From Comm. Statement:
                </Text>
                <Group spacing={4}>
                  <Badge size="sm" color="violet" variant="light">
                    {commStatementBids.length} bids
                  </Badge>
                  <Text size="sm">
                    {formatter.currency(commStatementTotal)}
                  </Text>
                </Group>
              </Group>
              {adjustmentBids.length > 0 && (
                <Group position="apart">
                  <Text size="sm" color="dimmed">
                    Adjustments:
                  </Text>
                  <Group spacing={4}>
                    <Badge size="sm" color="red" variant="light">
                      {adjustmentBids.length} bids
                    </Badge>
                    <Text size="sm" color="red">
                      {formatter.currency(adjustmentTotal)}
                    </Text>
                  </Group>
                </Group>
              )}
            </Stack>
          </Grid.Col>
        </Grid>
      </Paper>

      <Paper p="md" withBorder>
        <Title order={5} mb="md">{i18n.t("Actions")}</Title>

        <Stack spacing="md">
          <Group position="apart">
            <Box>
              <Text size="sm" weight={500}>
                Generate Full Report
              </Text>
              <Text size="xs" color="dimmed">
                Download an Excel file containing all payroll bid data
              </Text>
            </Box>
            <Button
              leftIcon={<IconDownload size={16} />}
              color="teal"
              onClick={handleGenerateReport}
              loading={reportLoading}
              disabled={totalBids === 0}
              style={{ minWidth: 230 }}
            >{i18n.t("Download Report")}</Button>
          </Group>

          <Group position="apart">
            <Box>
              <Text size="sm" weight={500}>
                {t("payroll2.confirmation.downloadWalletReports")}
              </Text>
              <Text size="xs" color="dimmed">
                Download an Excel file with wallet-level commission details only
              </Text>
            </Box>
            <Menu shadow="md" width={230} withinPortal>
              <Menu.Target>
                <Button
                  leftIcon={<IconDownload size={16} />}
                  rightIcon={<IconChevronDown size={16} />}
                  color="blue"
                  loading={walletReportLoading}
                  disabled={totalBids === 0}
                  style={{ minWidth: 230 }}
                >
                  {t("payroll2.confirmation.downloadWalletReports")}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  onClick={() => handleGenerateWalletReport(false)}
                  disabled={walletReportLoading || totalBids === 0}
                >
                  {t("payroll2.confirmation.walletReportAll")}
                </Menu.Item>
                <Menu.Item
                  onClick={() => handleGenerateWalletReport(true)}
                  disabled={walletReportLoading || totalBids === 0}
                >
                  {t("payroll2.confirmation.walletReportOnlySettled")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>

          <Group position="apart">
            <Box>
              <Text size="sm" weight={500}>
                {t("payroll2.confirmation.generateBankTransferFile")}
              </Text>
              <Text size="xs" color="dimmed">
                {t("payroll2.confirmation.generateBankTransferFileDesc")}
              </Text>
              {holdBids > 0 && (
                <Text size="xs" color="orange" mt={4}>
                  {t("payroll2.confirmation.holdingExcludedNote", {
                    count: holdBids,
                  })}
                </Text>
              )}
            </Box>
            <Menu shadow="md" width={200} withinPortal>
              <Menu.Target>
                <Button
                  leftIcon={<IconDownload size={16} />}
                  rightIcon={<IconChevronDown size={16} />}
                  color="violet"
                  loading={mriLoading}
                  disabled={totalBids === 0}
                  style={{ minWidth: 230 }}
                >
                  {t("payroll2.confirmation.generate")}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  onClick={handleGenerateHsbcMri}
                  disabled={mriLoading || totalBids === 0}
                >
                  {t("payroll2.confirmation.hsbcMri")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>

          <Group position="apart">
            <Box>
              <Text size="sm" weight={500}>
                Confirm Payroll
              </Text>
              <Text size="xs" color="dimmed">
                Mark all pending bids as confirmed and complete this payroll
              </Text>
            </Box>
            <Button
              leftIcon={<IconCheck size={16} />}
              color={isConfirmed ? "green" : "red"}
              onClick={handleConfirmClick}
              loading={confirmLoading}
              disabled={totalBids === 0 || isConfirmed || pendingBids === 0}
              style={{ minWidth: 230 }}
            >
              {isConfirmed ? "Completed" : "Confirm Payroll"}
            </Button>
          </Group>

          <Group position="apart">
            <Box>
              <Text size="sm" weight={500}>
                {t("payroll2.confirmation.settlePayroll")}
              </Text>
              <Text size="xs" color="dimmed">
                {t("payroll2.confirmation.settlePayrollDesc")}
              </Text>
            </Box>
            <Button
              leftIcon={<IconCircleCheck size={16} />}
              color={hasPaidOut ? "green" : "teal"}
              onClick={handleSettleClick}
              loading={settleLoading}
              disabled={!isConfirmed}
              style={{ minWidth: 230 }}
            >
              {hasPaidOut
                ? t("payroll2.confirmation.settled")
                : t("payroll2.confirmation.settle")}
            </Button>
          </Group>
        </Stack>

        {isConfirmed && (
          <Text size="xs" color="green" mt="md">
            ✓ This payroll has been confirmed.
          </Text>
        )}

        {hasPaidOut && (
          <Text size="xs" color="green" mt="md">
            {t("payroll2.confirmation.paidOutOn", {
              date: moment(form.values.payDate).format("YYYY-MM-DD"),
            })}
            {form?.values?.remark ? ` ${form.values.remark}` : ""}
          </Text>
        )}

        {walletsToAutoHold.length > 0 && !isConfirmed && (
          <Text size="xs" color="orange" mt="md">
            {t("payroll2.confirmation.autoHoldPreview", {
              count: walletsToAutoHold.length,
            })}
          </Text>
        )}

        {holdBids > 0 && !isConfirmed && (
          <Text size="xs" color="orange" mt="md">
            Note: {holdBids} bid(s) are on hold and will not be included in
            confirmation.
          </Text>
        )}
      </Paper>

      <Modal
        opened={confirmModalOpened}
        onClose={closeConfirmModal}
        title={
          <Title order={4} color="red">
            ⚠️ Confirm Payroll
          </Title>
        }
        size="md"
        centered
      >
        <Stack spacing="md">
          <Text size="sm" weight={500}>
            Are you sure you want to confirm this payroll?
          </Text>

          <List size="sm" spacing="xs">
            <List.Item>
              <Text color="red" weight={500}>
                This action cannot be undone.
              </Text>
            </List.Item>
            <List.Item>
              {pendingSettleBidCount > 0
                ? t("payroll2.confirmation.pendingWillSettle", {
                    count: pendingSettleBidCount,
                    amount: formatter.currency(pendingSettleAmount),
                  })
                : t("payroll2.confirmation.noPendingToSettle")}
            </List.Item>
            {walletsToAutoHold.length > 0 && (
              <List.Item>
                <Text color="orange" weight={500}>
                  {t("payroll2.confirmation.walletsWillAutoHold", {
                    count: walletsToAutoHold.length,
                  })}
                </Text>
                <List size="xs" spacing={2} mt={4} withPadding>
                  {walletsToAutoHold.map((w) => (
                    <List.Item key={w.walletId}>
                      {w.walletName}: {formatter.currency(w.netAmount || 0)} (
                      {w.bidCount} bid(s))
                    </List.Item>
                  ))}
                </List>
              </List.Item>
            )}
            <List.Item>
              {t("payroll2.confirmation.pendingTotal", {
                count: pendingBids,
                amount: formatter.currency(pendingAmount),
              })}
            </List.Item>
            <List.Item>
              The payroll will be marked as COMPLETED and no further edits will
              be allowed.
            </List.Item>
            {holdBids > 0 && (
              <List.Item>
                <Text color="orange">
                  {holdBids} bid(s) on hold will NOT be included.
                </Text>
              </List.Item>
            )}
          </List>

          <Checkbox
            mt="md"
            label="I understand that this action cannot be undone"
            checked={confirmChecked}
            onChange={(e) => setConfirmChecked(e.currentTarget.checked)}
          />

          <Group position="right" mt="md">
            <Button variant="default" onClick={closeConfirmModal}>{i18n.t("Cancel")}</Button>
            <Button
              color="red"
              onClick={handleConfirm}
              disabled={!confirmChecked}
              loading={confirmLoading}
            >
              Confirm Payroll
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={settleModalOpened}
        onClose={closeSettleModal}
        title={
          <Title order={4}>
            {t("payroll2.confirmation.settleModalTitle")}
          </Title>
        }
        size="md"
        centered
      >
        <Stack spacing="md">
          <DatePicker
            label={t("payroll2.confirmation.paidOutDate")}
            placeholder="Pick Date"
            value={settlePayDate}
            onChange={setSettlePayDate}
            allowFreeInput
            required
            withinPortal
          />
          <Textarea
            label={t("payroll2.confirmation.remark")}
            value={settleRemark}
            onChange={(e) => setSettleRemark(e.currentTarget.value)}
            minRows={3}
          />
          <Group position="right" mt="md">
            <Button variant="default" onClick={closeSettleModal}>{i18n.t("Cancel")}</Button>
            <Button
              color="teal"
              onClick={handleSettle}
              loading={settleLoading}
            >
              {t("payroll2.confirmation.settle")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={mriConfigWarningOpened}
        onClose={closeMriConfigWarning}
        title={
          <Title order={4} color="orange">
            {t("payroll2.confirmation.hsbcMriConfigWarningTitle")}
          </Title>
        }
        size="md"
        centered
      >
        <Stack spacing="md">
          <Text size="sm">{t("payroll2.confirmation.hsbcMriConfigWarningMessage")}</Text>
          <Group position="right" mt="md">
            <Button onClick={closeMriConfigWarning}>
              {t("payroll2.confirmation.hsbcMriConfigWarningOk")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};

export default ConfirmationTabContent;
